import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { baseURL } from "../../../api/api";

Modal.setAppElement("#root");

const CampaignDetailsModal = ({ isOpen, onClose, screenId, MonthYear }) => {
  const [campaignsByMonth, setCampaignsByMonth] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const fetchCampaignDetails = async (screenId, monthYear) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        `${baseURL}/screens/monthlyDetails/${screenId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ monthYear }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch campaign details");
      }

      const data = await response.json();

      // Sort campaigns by date in descending order (newest first)
      const sortedCampaigns = (data.campaignsByDate || []).sort((a, b) => {
        const dateA = parseDateString(a.date);
        const dateB = parseDateString(b.date);
        return dateB - dateA;
      });

      setCampaignsByMonth(sortedCampaigns);
      setSummary(data.summary || {});
    } catch (error) {
      setError(error.message);
      console.error("Error fetching campaign details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to parse DD/MM/YYYY format
  const parseDateString = (dateString) => {
    if (!dateString) return new Date(0);

    const parts = dateString.split("/");
    if (parts.length !== 3) return new Date(0);

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    return new Date(year, month, day);
  };

  // Format date to be more readable from DD/MM/YYYY format
  const formatDate = (dateString) => {
    try {
      const date = parseDateString(dateString);

      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Format online time for display
  const formatOnlineTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  useEffect(() => {
    if (isOpen && screenId) {
      fetchCampaignDetails(screenId, MonthYear);
    }
  }, [isOpen, screenId, MonthYear]);

  const handleCloseModal = () => {
    onClose();
    setCampaignsByMonth([]);
    setError(null);
    setSummary(null);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      contentLabel="Campaign Details"
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          zIndex: 1000,
        },
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          transform: "translate(-50%, -50%)",
          padding: "0",
          borderRadius: "4px",
          width: "90%",
          maxWidth: "1000px",
          maxHeight: "85vh",
          overflow: "hidden",
          border: "1px solid #ddd",
          backgroundColor: "white",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      {/* Header */}
      <div className="bg-white p-6 border-b border-gray-300">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Campaign Details
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              {MonthYear
                ? `Details for ${MonthYear}`
                : "Monthly Campaign Overview"}
            </p>
            {summary && (
              <div className="flex gap-4 mt-2 text-xs text-gray-600">
                <span>Valid Days: {summary.totalValidDays}</span>
                <span>Total Campaigns: {summary.totalCampaigns}</span>
                <span>Screen Online ≥ 6h</span>
              </div>
            )}
          </div>
          <button
            onClick={handleCloseModal}
            className="text-gray-500 hover:text-gray-700 text-2xl font-light"
          >
            &times;
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white p-6 max-h-[60vh] overflow-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="bg-gray-50 border border-gray-300 rounded p-4 max-w-md mx-auto">
              <div className="text-gray-900 font-medium">
                Error Loading Data
              </div>
              <p className="text-gray-600 text-sm mt-1">{error}</p>
              <button
                onClick={() => fetchCampaignDetails(screenId, MonthYear)}
                className="mt-3 bg-gray-900 text-white px-4 py-2 rounded text-sm hover:bg-gray-800 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : campaignsByMonth.length > 0 ? (
          <div className="bg-white border border-gray-300 rounded overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 bg-gray-100 text-gray-900 font-semibold text-sm border-b border-gray-300">
              <div className="col-span-3 p-4 border-r border-gray-300">
                Date
              </div>
              <div className="col-span-5 p-4 border-r border-gray-300">
                Campaigns
              </div>
              <div className="col-span-2 p-4 border-r border-gray-300 text-center">
                Online Time
              </div>
              <div className="col-span-2 p-4 text-center">Total</div>
            </div>

            {/* Table Rows */}
            <div className="divide-y divide-gray-200">
              {campaignsByMonth.map((campaign, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 hover:bg-gray-50 transition-colors"
                >
                  {/* Date Column */}
                  <div className="col-span-3 p-4 border-r border-gray-300">
                    <div
                      className={`font-medium text-sm ${
                        formatDate(campaign.date) === "Invalid Date"
                          ? "text-red-600"
                          : "text-gray-900"
                      }`}
                    >
                      {formatDate(campaign.date)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {campaign.date}
                    </div>
                  </div>

                  {/* Campaigns Column */}
                  <div className="col-span-5 p-4 border-r border-gray-300">
                    <div className="flex flex-wrap gap-2">
                      {campaign.campaigns.map((name, index) => (
                        <span
                          key={index}
                          className="bg-white text-gray-900 px-3 py-1 rounded border border-gray-300 text-sm font-normal"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Online Time Column */}
                  <div className="col-span-2 p-4 border-r border-gray-300 flex items-center justify-center">
                    <span
                      className={`text-sm font-medium ${
                        campaign.onlineHours >= 6
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                      title={`${campaign.onlineHours} hours`}
                    >
                      {formatOnlineTime(campaign.onlineTime)}
                    </span>
                  </div>

                  {/* Count Column */}
                  <div className="col-span-2 p-4 flex items-center justify-center">
                    <span className="bg-gray-900 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-medium">
                      {campaign.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 border border-gray-300 rounded bg-gray-50">
            <div className="text-4xl mb-3 text-gray-400">📊</div>
            <h3 className="text-gray-900 font-medium">No Campaign Data</h3>
            <p className="text-gray-600 text-sm mt-1">
              No campaign details available for the selected period or screen
              didn't run for sufficient time.
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-100 px-6 py-3 border-t border-gray-300">
        <div className="flex justify-between items-center text-base font-bold text-gray-600">
          <span>Total Valid Days: {summary?.totalValidDays || 0}</span>
          <span>Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>
    </Modal>
  );
};

export default CampaignDetailsModal;
